import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { formatInt, formatStatusLabel } from "../lib/format";
import { Search, Zap, ArrowUpRight } from "lucide-react";

/* ─────────── Reveal-on-scroll helper ─────────── */

function useInView({ threshold = 0.15, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, as: As = "div", style, ...rest }) {
  const [ref, inView] = useInView();
  return (
    <As
      ref={ref}
      className={`wn-reveal${inView ? " is-visible" : ""}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </As>
  );
}

/* ─────────── Map marker styles ─────────── */

const STATUS_FILL = {
  vacant_available:  { fill: "#B14E2F", outline: "#8F3C23" },
  structure_present: { fill: "transparent", outline: "#8F3C23" },
  in_review:         { fill: "#4F6149", outline: "#3D4D38" },
  under_contract:    { fill: "#1B1F4B", outline: "#1B1F4B" },
};

const makeIcon = (status) => {
  const { fill, outline } = STATUS_FILL[status] || STATUS_FILL.vacant_available;
  return L.divIcon({
    className: "wn-parcel-marker",
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    html: `<span style="display:block;width:12px;height:12px;background:${fill};border:2px solid ${outline};border-radius:2px;"></span>`,
  });
};

/* ─────────── Filter constants ─────────── */

const STATUS_CHIPS = [
  { key: "all",                label: "All" },
  { key: "vacant_available",   label: "Available" },
  { key: "in_review",          label: "In review" },
  { key: "under_contract",     label: "Under contract" },
  { key: "structure_present",  label: "Has structure" },
];

const NEIGHBORHOODS = [
  { key: "all",                  label: "All neighborhoods" },
  { key: "Near Eastside",        label: "Near Eastside" },
  { key: "Martindale-Brightwood", label: "Martindale-Brightwood" },
];

/* ─────────── Page ─────────── */

export default function Parcels() {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [hood, setHood] = useState("all");
  const [tenDayOnly, setTenDayOnly] = useState(false);
  const [q, setQ] = useState("");
  const [mapMode, setMapMode] = useState("street");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/parcels", { params: { only_demo: true, limit: 500 } })
      .then((r) => !cancelled && setParcels(r.data))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    let xs = parcels;
    if (status !== "all") xs = xs.filter((p) => p.status === status);
    if (hood !== "all") xs = xs.filter((p) => p.neighborhood === hood);
    if (tenDayOnly)
      xs = xs.filter((p) => (p.constraints || []).includes("ten_day_path_eligible"));
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      xs = xs.filter((p) => {
        const id = `p-${String(p.parcel_id).padStart(5, "0").slice(-5)}`;
        const street = (p.address_parts?.street || p.address || "").toLowerCase();
        return id.includes(needle) || street.includes(needle);
      });
    }
    return xs;
  }, [parcels, status, hood, tenDayOnly, q]);

  const counts = useMemo(() => ({
    total: parcels.length,
    vacant: parcels.filter((p) => p.status === "vacant_available").length,
    tenday: parcels.filter((p) => (p.constraints || []).includes("ten_day_path_eligible")).length,
    hoods: new Set(parcels.map((p) => p.neighborhood)).size,
  }), [parcels]);

  const center = useMemo(() => {
    if (!parcels.length) return [39.793, -86.130];
    const sum = parcels.reduce(
      (a, p) => ({ lat: a.lat + (p.geo?.lat || 0), lng: a.lng + (p.geo?.lng || 0) }),
      { lat: 0, lng: 0 }
    );
    return [sum.lat / parcels.length, sum.lng / parcels.length];
  }, [parcels]);

  const resetAll = () => {
    setStatus("all");
    setHood("all");
    setTenDayOnly(false);
    setQ("");
  };

  return (
    <section className="animate-fade-in-up" data-testid="parcels-page">
      {/* ──────────── Hero ──────────── */}
      <Reveal>
        <Hero counts={counts} />
      </Reveal>

      {/* ──────────── Toolbar ──────────── */}
      <Reveal delay={80}>
        <Toolbar
          q={q} onQ={setQ}
          status={status} onStatus={setStatus}
          hood={hood} onHood={setHood}
          tenDayOnly={tenDayOnly} onTenDayOnly={setTenDayOnly}
          onReset={resetAll}
        />
      </Reveal>

      {/* ──────────── Map + List ──────────── */}
      <div
        className="grid lg:grid-cols-[1.4fr_1fr] gap-x-10 mt-8"
        style={{ rowGap: 18 }}
      >
        {/* Row 1 — fixed height. Both columns stretch to this. */}
        <Reveal style={{ height: 600, display: "flex", flexDirection: "column" }}>
          <MapPanel
            center={center}
            parcels={filtered}
            mode={mapMode}
            onMode={setMapMode}
          />
        </Reveal>

        <Reveal
          delay={100}
          style={{
            height: 600,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <ResultsHeader
            count={filtered.length}
            total={parcels.length}
            loading={loading}
          />
          <ParcelList
            loading={loading}
            parcels={filtered}
            onReset={resetAll}
          />
        </Reveal>

        {/* Row 2 — caption + legend, under the map column only */}
        <div>
          {mapMode === "aerial" && (
            <div
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 12,
                lineHeight: 1.55,
                marginBottom: 12,
              }}
              data-testid="aerial-caption"
            >
              Aerial imagery &copy; Esri / Maxar &middot; recent (2-3 years).
              Imagery may not reflect demolitions or new construction since
              the survey.
            </div>
          )}
          <MapLegend />
        </div>
        <div />
      </div>

      {/* ──────────── § 02 · Bridge CTA ──────────── */}
      <Reveal>
        <BridgeCTA />
      </Reveal>
    </section>
  );
}

/* ─────────── Hero ─────────── */

function Hero({ counts }) {
  return (
    <header
      className="grid lg:grid-cols-[1.35fr_1fr] gap-14 items-end"
      style={{ paddingBottom: 28 }}
    >
      <div className="max-w-[760px]">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 28,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            § 01 · Vacant parcel inventory
          </span>
        </div>
        <h1
          className="mt-5"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: "clamp(40px, 5.6vw, 60px)",
            fontWeight: 500,
            lineHeight: 1.04,
            color: "#1B1F4B",
            letterSpacing: "-0.005em",
            margin: 0,
          }}
        >
          Indianapolis infill,{" "}
          <span style={{ fontStyle: "italic", color: "#8F3C23" }}>
            surveyed and ready
          </span>
          .
        </h1>
        <p
          className="mt-5 max-w-[700px]"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 19,
            lineHeight: 1.55,
          }}
        >
          Every parcel below has been classified by neighborhood, dimensions,
          zoning, and known constraints — so designs from the marketplace can
          be matched at a glance.
        </p>
        <span
          aria-hidden
          style={{
            display: "block",
            width: 80,
            height: 2,
            background: "#B14E2F",
            marginTop: 36,
            marginBottom: 22,
          }}
        />
        <div className="flex items-stretch flex-wrap" data-testid="parcels-stats">
          {[
            { value: counts.total,  label: "Surveyed parcels" },
            { value: counts.vacant, label: "Available now" },
            { value: counts.tenday, label: "10-Day eligible" },
            { value: counts.hoods,  label: "Active neighborhoods" },
          ].map((c, i) => (
            <div
              key={c.label}
              style={{
                paddingRight: 32,
                paddingLeft: i === 0 ? 0 : 32,
                borderLeft: i === 0 ? "none" : "1px solid #D9D8E4",
              }}
            >
              <div
                className="tabular-nums"
                style={{
                  fontWeight: 700,
                  fontSize: 28,
                  color: "#1B1F4B",
                  lineHeight: 1,
                }}
              >
                {c.value || 0}
              </div>
              <div className="wn-label mt-2">{c.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right-rail editorial dateline */}
      <aside
        className="hidden lg:block"
        style={{
          borderLeft: "1px solid #D9D8E4",
          paddingLeft: 32,
          paddingBottom: 8,
        }}
        aria-hidden
      >
        <div className="wn-label">Volume 01 · Survey</div>
        <div
          className="mt-2"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: 17,
            color: "#1B1F4B",
            lineHeight: 1.35,
          }}
        >
          A working inventory
          <br />
          of vacant lots in two
          <br />
          target neighborhoods.
        </div>
        <p
          className="mt-5"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13.5,
            lineHeight: 1.55,
            maxWidth: 280,
          }}
        >
          Status, zoning, and dimensions are refreshed quarterly from city
          records. Selecting a parcel routes through to its detail page with
          design-fit recommendations.
        </p>
      </aside>
    </header>
  );
}

/* ─────────── Toolbar ─────────── */

function Toolbar({
  q, onQ, status, onStatus, hood, onHood, tenDayOnly, onTenDayOnly, onReset,
}) {
  return (
    <div
      data-testid="parcels-toolbar"
      style={{
        borderTop: "1px solid #D9D8E4",
        borderBottom: "1px solid #D9D8E4",
        marginTop: 24,
      }}
    >
      {/* Row 1 — search + status chips */}
      <div
        className="flex items-center"
        style={{
          padding: "16px 0",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ position: "relative", width: 320, flexShrink: 0 }}>
          <Search
            size={15}
            color="#6C6B85"
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          />
          <input
            value={q}
            onChange={(e) => onQ(e.target.value)}
            placeholder="Parcel ID or street address."
            className="wn-input"
            style={{ paddingLeft: 34, width: "100%" }}
            data-testid="parcel-search"
          />
        </div>
        <div
          className="flex flex-wrap items-center"
          data-testid="status-chips"
          style={{ gap: 8 }}
        >
          {STATUS_CHIPS.map((c) => {
            const active = status === c.key;
            return (
              <button
                key={c.key}
                onClick={() => onStatus(c.key)}
                data-testid={`status-chip-${c.key}`}
                style={{
                  padding: "5px 12px",
                  borderRadius: 999,
                  border: `1px solid ${active ? "#B14E2F" : "#D9D8E4"}`,
                  backgroundColor: active ? "#B14E2F" : "transparent",
                  color: active ? "#FBFAFD" : "#1B1F4B",
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: "pointer",
                  transition: "all 160ms ease",
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Row 2 — secondary controls (neighborhood, 10-day, reset) */}
      <div
        className="flex items-center flex-wrap"
        style={{
          padding: "12px 0 14px",
          borderTop: "1px solid #ECEBF1",
          gap: 20,
        }}
      >
        <label
          className="inline-flex items-center"
          style={{ gap: 10, flexShrink: 0 }}
        >
          <span className="wn-label" style={{ color: "#6C6B85" }}>
            Neighborhood
          </span>
          <select
            value={hood}
            onChange={(e) => onHood(e.target.value)}
            className="wn-input"
            data-testid="parcel-neighborhood"
            style={{ minWidth: 200 }}
          >
            {NEIGHBORHOODS.map((n) => (
              <option key={n.key} value={n.key}>{n.label}</option>
            ))}
          </select>
        </label>

        <label
          className="inline-flex items-center"
          style={{ gap: 8, cursor: "pointer", userSelect: "none", flexShrink: 0 }}
          data-testid="ten-day-toggle"
        >
          <input
            type="checkbox"
            checked={tenDayOnly}
            onChange={(e) => onTenDayOnly(e.target.checked)}
            style={{ accentColor: "#B14E2F" }}
          />
          <span
            className="inline-flex items-center"
            style={{
              color: "#4F6149",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 11.5,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              gap: 5,
            }}
          >
            <Zap size={12} strokeWidth={2.5} />
            10-Day only
          </span>
        </label>

        <button
          onClick={onReset}
          className="wn-btn wn-btn-tertiary"
          data-testid="parcel-filter-reset"
          style={{ height: "auto", padding: 0, fontSize: 12.5, marginLeft: "auto" }}
        >
          Reset filters →
        </button>
      </div>
    </div>
  );
}

/* ─────────── Map panel ─────────── */

const TILES = {
  street: {
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png",
    attribution: "&copy; OpenStreetMap &copy; CARTO",
    subdomains: ["a", "b", "c", "d"],
    maxZoom: 19,
  },
  aerial: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      "Imagery &copy; Esri, Maxar, Earthstar Geographics, USDA · Surveyed 2025",
    subdomains: [],
    maxZoom: 19,
  },
};

function MapPanel({ center, parcels, mode, onMode }) {
  const tiles = TILES[mode];
  return (
    <div
      data-testid="parcels-map-panel"
      style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
    >
      {/* Toggle header — sits above the map, right-aligned */}
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: 10, flexShrink: 0 }}
      >
        <span className="wn-label" style={{ color: "#6C6B85" }}>
          Geographic view
        </span>
        <div
          className="inline-flex"
          role="tablist"
          aria-label="Map basemap"
          data-testid="map-mode-toggle"
          style={{
            border: "1px solid #D9D8E4",
            borderRadius: 999,
            padding: 2,
            backgroundColor: "#FBFAFD",
          }}
        >
          {[
            { key: "street", label: "Street" },
            { key: "aerial", label: "Aerial" },
          ].map((m) => {
            const active = mode === m.key;
            return (
              <button
                key={m.key}
                role="tab"
                aria-selected={active}
                onClick={() => onMode(m.key)}
                data-testid={`map-mode-${m.key}`}
                style={{
                  padding: "5px 14px",
                  borderRadius: 999,
                  border: "none",
                  backgroundColor: active ? "#1B1F4B" : "transparent",
                  color: active ? "#FBFAFD" : "#1B1F4B",
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: 11.5,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  transition: "background-color 180ms ease, color 180ms ease",
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
          border: "1px solid #D9D8E4",
          borderRadius: 4,
        }}
        data-testid="parcels-map"
      >
        <MapContainer
          center={center}
          zoom={14}
          scrollWheelZoom
          className={`wn-leaflet-map ${mode === "aerial" ? "wn-aerial" : ""}`}
          style={{ height: "100%", width: "100%" }}
        >
          <InvalidateSizeOnResize />
          <TileLayer
            key={mode}
            attribution={tiles.attribution}
            url={tiles.url}
            subdomains={tiles.subdomains}
            maxZoom={tiles.maxZoom}
          />
          <RecenterOnData parcels={parcels} />
          {parcels.map((p) =>
            p.geo?.lat && p.geo?.lng ? (
              <Marker key={p.id} position={[p.geo.lat, p.geo.lng]} icon={makeIcon(p.status)}>
                <Popup>
                  <div style={{ fontFamily: '"Nunito Sans", sans-serif', minWidth: 200 }}>
                    <div
                      style={{
                        fontFamily: '"Fraunces", Georgia, serif',
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#1B1F4B",
                      }}
                    >
                      {p.address}
                    </div>
                    <div style={{ color: "#6C6B85", fontSize: 12, marginTop: 4 }}>
                      {p.neighborhood}
                    </div>
                    <div className="tnum" style={{ fontSize: 12, color: "#272636", marginTop: 6 }}>
                      {p.width_ft}′ × {p.depth_ft}′ · {formatInt(p.area_sqft)} sqft · {p.zoning}
                    </div>
                    <Link
                      to={`/parcels/${p.id}`}
                      style={{
                        display: "inline-block",
                        marginTop: 10,
                        color: "#B14E2F",
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      Open detail →
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ) : null
          )}
        </MapContainer>
      </div>
    </div>
  );
}

/* Inline hairline legend — used in row 2 of the map+list grid. */
function MapLegend() {
  return (
    <div
      className="flex flex-wrap items-center"
      style={{ gap: 18, columnGap: 22 }}
      data-testid="map-legend"
    >
      <span className="wn-label" style={{ color: "#6C6B85" }}>
        Legend
      </span>
      {[
        { status: "vacant_available",   label: "Vacant · available" },
        { status: "structure_present",  label: "Has structure" },
        { status: "in_review",          label: "In review" },
        { status: "under_contract",     label: "Under contract" },
      ].map((it) => {
        const { fill, outline } = STATUS_FILL[it.status];
        return (
          <span key={it.status} className="inline-flex items-center" style={{ gap: 8 }}>
            <span
              aria-hidden
              style={{
                width: 11,
                height: 11,
                backgroundColor: fill,
                border: `2px solid ${outline}`,
                borderRadius: 2,
                display: "inline-block",
              }}
            />
            <span style={{ color: "#272636", fontSize: 12.5 }}>{it.label}</span>
          </span>
        );
      })}
    </div>
  );
}

/* Tell Leaflet to recalculate its container size once mounted/resized so
   the map renders at full height when the parent grid stretches. */
function InvalidateSizeOnResize() {
  const map = useMap();
  useEffect(() => {
    // Initial invalidate after first paint
    const t = setTimeout(() => map.invalidateSize(), 80);
    // Track container resizes (e.g. window resize, flex re-layout)
    const ro = new ResizeObserver(() => map.invalidateSize());
    const container = map.getContainer();
    if (container) ro.observe(container);
    return () => {
      clearTimeout(t);
      ro.disconnect();
    };
  }, [map]);
  return null;
}

/* ─────────── List ─────────── */

function ResultsHeader({ count, total, loading }) {
  return (
    <div
      className="flex items-baseline justify-between"
      style={{ paddingBottom: 12, borderBottom: "1px solid #D9D8E4" }}
      data-testid="parcels-results-header"
    >
      <div>
        <span
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
          }}
        >
          Showing{" "}
        </span>
        <strong
          className="tabular-nums"
          style={{
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            color: "#1B1F4B",
            fontSize: 18,
          }}
        >
          {loading ? "…" : count}
        </strong>
        <span
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
          }}
        >
          {" "}of {total} {total === 1 ? "parcel" : "parcels"}
        </span>
      </div>
      <span className="wn-label" style={{ color: "#6C6B85" }}>
        Sorted by address
      </span>
    </div>
  );
}

function ParcelList({ loading, parcels, onReset }) {
  if (loading) {
    return (
      <div style={{ color: "#6C6B85", fontStyle: "italic", padding: "20px 0" }}>
        Loading parcels…
      </div>
    );
  }
  if (parcels.length === 0) {
    return (
      <div
        className="text-center"
        style={{ padding: "40px 16px", color: "#6C6B85" }}
        data-testid="parcels-empty"
      >
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 20,
            color: "#1B1F4B",
            marginBottom: 6,
          }}
        >
          No parcels match.
        </div>
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            fontSize: 14,
            marginBottom: 14,
          }}
        >
          Try removing a filter, or widen the search.
        </div>
        <button
          onClick={onReset}
          className="wn-btn wn-btn-tertiary"
          style={{ height: "auto", padding: 0, fontSize: 13 }}
        >
          Reset all filters →
        </button>
      </div>
    );
  }
  return (
    <div
      style={{
        position: "relative",
        flex: 1,
        minHeight: 460,
        overflow: "hidden",
      }}
      data-testid="parcel-list-wrap"
    >
      <div
        style={{
          height: "100%",
          overflowY: "auto",
          paddingRight: 8,
          paddingBottom: 32,
          scrollbarGutter: "stable",
        }}
        data-testid="parcel-list"
      >
        {parcels.map((p) => (
          <ParcelRow key={p.id} p={p} />
        ))}
      </div>
      {/* Bottom fade so the last row doesn't crop awkwardly */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 36,
          background:
            "linear-gradient(to bottom, rgba(251,250,253,0) 0%, #FBFAFD 70%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

function ParcelRow({ p }) {
  const id = `P-${String(p.parcel_id).padStart(5, "0").slice(-5)}`;
  const tenDay = (p.constraints || []).includes("ten_day_path_eligible");
  return (
    <Link
      to={`/parcels/${p.id}`}
      data-testid={`parcel-list-item-${p.id}`}
      className="wn-parcel-row"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "start",
        gap: 16,
        padding: "16px 4px",
        borderBottom: "1px solid #ECEBF1",
        textDecoration: "none",
        color: "inherit",
        transition: "background-color 160ms ease-out",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(177,78,47,0.04)")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
    >
      <div style={{ minWidth: 0 }}>
        <div className="wn-label" style={{ color: "#B14E2F" }}>
          {id}
        </div>
        <div
          className="mt-1"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 17,
            color: "#1B1F4B",
            lineHeight: 1.2,
          }}
        >
          {(p.address_parts?.street) || p.address}
        </div>
        <div
          className="mt-1"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13.5,
            lineHeight: 1.45,
          }}
        >
          {p.neighborhood} · {p.width_ft}′ × {p.depth_ft}′ · {formatInt(p.area_sqft)} sq ft
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
        <StatusBadge status={p.status} />
        {tenDay && (
          <span
            className="inline-flex items-center"
            style={{
              color: "#4F6149",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 10.5,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              gap: 4,
            }}
            data-testid="ten-day-badge"
          >
            <Zap size={11} strokeWidth={2.5} />
            10-Day
          </span>
        )}
      </div>
    </Link>
  );
}

/* ─────────── Bridge CTA ─────────── */

function BridgeCTA() {
  return (
    <div
      data-testid="parcels-bridge-cta"
      className="mt-16 grid lg:grid-cols-[1fr_auto] items-center gap-8"
      style={{
        borderTop: "1px solid #D9D8E4",
        borderBottom: "1px solid #D9D8E4",
        padding: "28px 0",
      }}
    >
      <div>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 28,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            § 02 · Match a design
          </span>
        </div>
        <h2
          className="mt-3 max-w-[720px]"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: "clamp(24px, 2.8vw, 32px)",
            lineHeight: 1.15,
            color: "#1B1F4B",
            letterSpacing: "-0.005em",
            margin: 0,
          }}
        >
          Have a fit in mind?{" "}
          <span style={{ fontStyle: "italic", color: "#8F3C23" }}>
            Match a marketplace design
          </span>{" "}
          to any parcel.
        </h2>
        <p
          className="mt-3 max-w-[680px]"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 15.5,
            lineHeight: 1.55,
          }}
        >
          Open any parcel to see pre-reviewed designs already vetted for its
          width, zoning, and neighborhood character — or browse the catalog
          first and check fit afterwards.
        </p>
      </div>
      <Link
        to="/marketplace"
        className="wn-btn wn-btn-accent"
        data-testid="parcels-to-marketplace"
        style={{ padding: "12px 22px", fontSize: 14 }}
      >
        Browse the marketplace
        <ArrowUpRight size={16} />
      </Link>
    </div>
  );
}

/* ─────────── Helpers ─────────── */

function RecenterOnData({ parcels }) {
  const map = useMap();
  useEffect(() => {
    if (!parcels.length) return;
    const valid = parcels.filter((p) => p.geo?.lat && p.geo?.lng);
    if (!valid.length) return;
    const bounds = L.latLngBounds(valid.map((p) => [p.geo.lat, p.geo.lng]));
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 15 });
  }, [parcels, map]);
  return null;
}

function StatusBadge({ status }) {
  const styles = {
    vacant_available:  { bg: "rgba(177,78,47,0.10)", bd: "rgba(177,78,47,0.40)", fg: "#8F3C23" },
    structure_present: { bg: "rgba(108,107,133,0.10)", bd: "rgba(108,107,133,0.40)", fg: "#5B5A72" },
    in_review:         { bg: "rgba(79,97,73,0.10)",  bd: "rgba(79,97,73,0.40)",  fg: "#4F6149" },
    under_contract:    { bg: "rgba(27,31,75,0.08)",  bd: "rgba(27,31,75,0.45)",  fg: "#1B1F4B" },
  }[status] || {};
  return (
    <span
      style={{
        backgroundColor: styles.bg,
        border: `1px solid ${styles.bd}`,
        color: styles.fg,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        fontFamily: '"Nunito Sans", sans-serif',
        whiteSpace: "nowrap",
      }}
    >
      {formatStatusLabel(status)}
    </span>
  );
}
